import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Building2, Upload, Trash2, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BranchPrices() {
  const [branch, setBranch] = useState("MADRID");
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const fetchPrices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/prices/branch`, { params: { branch } });
      setPrices(res.data || []);
    } catch {
      toast.error("Error cargando precios");
    } finally {
      setLoading(false);
    }
  }, [branch]);

  useEffect(() => { fetchPrices(); }, [fetchPrices]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const filtered = search
    ? prices.filter((p) => (p.erp_code || "").toLowerCase().includes(search))
    : prices;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/prices/branch/upload?branch=${branch}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Importados: ${res.data.upserted} precios (${res.data.errors} errores)`);
      fetchPrices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error en la importación");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (erp_code) => {
    try {
      await axios.delete(`${API}/admin/prices/branch`, { params: { branch, erp_code } });
      setPrices((prev) => prev.filter((p) => p.erp_code !== erp_code));
      toast.success("Eliminado");
    } catch {
      toast.error("Error eliminando precio");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="branch-prices-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Precios por Sede</h1>
              <p className="text-slate-500">{prices.length} precios cargados</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              className="h-10 px-3 border border-slate-200 rounded-md text-sm font-medium"
              data-testid="branch-select"
            >
              <option value="MADRID">MADRID</option>
              <option value="GUADALAJARA">GUADALAJARA</option>
            </select>

            <label className="cursor-pointer">
              <input type="file" accept=".csv" className="hidden" onChange={handleUpload} data-testid="branch-csv-input" />
              <span className={`inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm font-medium text-white ${uploading ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'} transition-colors`}>
                <Upload className="w-4 h-4" />
                {uploading ? "Importando..." : "Importar CSV"}
              </span>
            </label>
          </div>
        </div>

        {/* Help text */}
        <Card className="bg-indigo-50 border-indigo-200">
          <CardContent className="p-4 text-sm text-indigo-800">
            <strong>Formato CSV:</strong> erp_code,price (separado por comas). Ejemplo:<br />
            <code className="bg-white/60 px-1 rounded">erp_code,price</code><br />
            <code className="bg-white/60 px-1 rounded">ART-001,12.50</code><br />
            <code className="bg-white/60 px-1 rounded">ART-002,8.90</code>
          </CardContent>
        </Card>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar por código..."
            className="pl-10"
            data-testid="branch-price-search"
          />
        </div>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Tarifas {branch}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-center py-12 text-slate-500">
                {search ? "Sin resultados" : "No hay precios cargados para esta sede"}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código ERP</TableHead>
                      <TableHead>Precio</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((p) => (
                      <TableRow key={p.erp_code} data-testid={`branch-price-row-${p.erp_code}`}>
                        <TableCell className="font-mono text-sm">{p.erp_code}</TableCell>
                        <TableCell className="font-semibold">{Number(p.price).toFixed(2)}€</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(p.erp_code)}
                            data-testid={`delete-branch-price-${p.erp_code}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
